import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

type ClientQuoteModalClient = {
  id: number | string;
  idnumber?: string;
  fullname?: string;
  quote?: number | string | null;
  collect?: {
    name?: string;
    office?: {
      name?: string;
    };
  };
};

interface ClientQuoteModalProps {
  open: boolean;
  client: ClientQuoteModalClient | null;
  onClose: () => void;
  onSaved?: (nextQuote: number) => void;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
}

type ClientDetailResponse = {
  client?: ClientQuoteModalClient | null;
};

const toNumericQuote = (value: unknown) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0 ? numericValue : 0;
};

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90vw',
  maxWidth: 500,
  height: '80vh',
  maxHeight: 500,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 2,
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'space-between',
  overflowY: 'auto' as const,
};

const ClientQuoteModal: React.FC<ClientQuoteModalProps> = ({
  open,
  client,
  onClose,
  onSaved,
  showAlert,
}) => {
  const [quote, setQuote] = useState('0');
  const [isSaving, setIsSaving] = useState(false);
  const [resolvedClient, setResolvedClient] = useState<ClientQuoteModalClient | null>(client);

  useEffect(() => {
    setResolvedClient(client);
  }, [client]);

  useEffect(() => {
    if (!open || !client?.id) {
      return;
    }

    let cancelled = false;

    const fetchClientDetail = async () => {
      try {
        const { data } = await axios.get<ClientDetailResponse>(`/clients/${client.id}`);
        if (!cancelled && data?.client) {
          setResolvedClient(data.client);
        }
      } catch (error) {
        if (!cancelled) {
          setResolvedClient(client);
        }
      }
    };

    fetchClientDetail();

    return () => {
      cancelled = true;
    };
  }, [client, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setQuote(String(toNumericQuote((resolvedClient ?? client)?.quote)));
  }, [client, open, resolvedClient]);

  const effectiveClient = resolvedClient ?? client;

  const clientLabel = useMemo(() => {
    if (!effectiveClient) {
      return '';
    }

    return `${effectiveClient.fullname ?? 'Cliente'}${effectiveClient.idnumber ? ` (${effectiveClient.idnumber})` : ''}`;
  }, [effectiveClient]);

  const handleSave = async () => {
    if (!effectiveClient?.id) {
      return;
    }

    const numericQuote = Number(quote);

    if (!Number.isFinite(numericQuote) || numericQuote < 0) {
      showAlert('El cupo debe ser un número mayor o igual a 0.', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`/clients/${effectiveClient.id}/quote`, { quote: numericQuote });
      onSaved?.(numericQuote);
      showAlert('Cupo actualizado correctamente.', 'success');
      onClose();
    } catch (error: any) {
      showAlert(
        error?.response?.data?.message || 'No fue posible actualizar el cupo del cliente.',
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            bgcolor: 'primary.main',
            color: 'white',
          }}
        >
          <Typography variant="h6">Modificar cupo</Typography>
          <IconButton onClick={handleClose} disabled={isSaving} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ p: 3, overflowY: 'auto', flexGrow: 1 }}>
          <Typography variant="body1">{clientLabel}</Typography>
          <Divider sx={{ mb: 2, mt: 2 }} />
          <TextField
            fullWidth
            size="small"
            label="Oficina"
            value={effectiveClient?.collect?.office?.name || ''}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Cobro"
            value={effectiveClient?.collect?.name || ''}
            InputProps={{ readOnly: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Cupo cliente"
            value={quote}
            onChange={(event) => setQuote(event.target.value)}
            inputProps={{ min: 0 }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Button onClick={handleClose} color="secondary" variant="outlined" disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={isSaving}>
            Guardar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ClientQuoteModal;
